import { useCallback, useMemo, useState, type DragEvent } from 'react'
import { confirmDelete } from '@util'
import {
  isNotDefined,
  type DashboardDataWithEditable,
  type DashboardId,
  type DashboardListInput,
  type UserId,
} from '@terros/common'
import { useToastError } from '@hooks'
import { useDashboardCache, useRemoveDashboard, useTranslation, useUpdateDashboard } from '@front/model'
import { getNewOrder } from './reorder'
import {
  flattenDashboardGroups,
  getDashboardGroup,
  getDisplayedDashboardGroups,
  getOrderedDashboardGroups,
  type DashboardGroup,
  type DashboardGroups,
} from './dashboardGrouping'

type Props = {
  listInput?: DashboardListInput
  scopedDashboards: DashboardDataWithEditable[]
  viewerUserId?: UserId
}

type DashboardListState = {
  deletingDashboardId?: DashboardId
  displayDashboardsByGroup: DashboardGroups
  draggingDashboardId?: DashboardId
  handleDelete: (dashboard: DashboardDataWithEditable) => Promise<void>
  handleDragEnd: () => void
  handleDragOver: (event: DragEvent<HTMLDivElement>, group: DashboardGroup, index: number) => void
  handleDragStart: (dashboard: DashboardDataWithEditable, index: number) => void
  handleDrop: (event: DragEvent<HTMLDivElement>, group: DashboardGroup) => void
  hasEditableDashboard: boolean
  isRemoving: boolean
  isReordering: boolean
  orderedDashboards: DashboardDataWithEditable[]
  resetInteractionState: () => void
  startReordering: () => void
  stopReordering: () => void
}

export function useDashboardList({ listInput, scopedDashboards, viewerUserId }: Props): DashboardListState {
  const { t } = useTranslation()
  const { update, error: updateError } = useUpdateDashboard(listInput)
  const { remove, isRemoving, error: removeError } = useRemoveDashboard(listInput)
  const cache = useDashboardCache(listInput)
  useToastError(updateError)
  useToastError(removeError)

  const [isReordering, setIsReordering] = useState(false)
  const [draggingDashboard, setDraggingDashboard] = useState<DashboardDataWithEditable | undefined>(undefined)
  const [dragToIndex, setDragToIndex] = useState<number | undefined>(undefined)
  const [deletingDashboardId, setDeletingDashboardId] = useState<DashboardId | undefined>(undefined)
  const [pendingOrders, setPendingOrders] = useState<Map<DashboardId, number>>(() => new Map())

  const orderedDashboardsByGroup = useMemo(
    () => getOrderedDashboardGroups(scopedDashboards, pendingOrders, viewerUserId),
    [pendingOrders, scopedDashboards, viewerUserId]
  )
  const orderedDashboards = useMemo(() => flattenDashboardGroups(orderedDashboardsByGroup), [orderedDashboardsByGroup])
  const displayDashboardsByGroup = useMemo(
    () => getDisplayedDashboardGroups(orderedDashboardsByGroup, draggingDashboard, dragToIndex),
    [dragToIndex, draggingDashboard, orderedDashboardsByGroup]
  )

  const stopReordering = useCallback((): void => {
    setIsReordering(false)
    setDraggingDashboard(undefined)
    setDragToIndex(undefined)
  }, [])

  const startReordering = useCallback((): void => {
    setIsReordering(true)
  }, [])

  const handleDragEnd = useCallback((): void => {
    setDraggingDashboard(undefined)
    setDragToIndex(undefined)
  }, [])

  const handleDragStart = useCallback(
    (dashboard: DashboardDataWithEditable, index: number): void => {
      if (!isReordering || dashboard.editable !== true || dashboard.home === true) return
      setDraggingDashboard(dashboard)
      setDragToIndex(index)
    },
    [isReordering]
  )

  const handleDragOver = useCallback(
    (event: DragEvent<HTMLDivElement>, group: DashboardGroup, index: number): void => {
      event.preventDefault()
      if (isNotDefined(draggingDashboard)) return
      if (getDashboardGroup(draggingDashboard) !== group) return
      if (index === dragToIndex) return
      setDragToIndex(index)
    },
    [dragToIndex, draggingDashboard]
  )

  const handleDrop = useCallback(
    (event: DragEvent<HTMLDivElement>, group: DashboardGroup): void => {
      event.preventDefault()
      const movedDashboard = draggingDashboard
      const toIndex = dragToIndex
      handleDragEnd()

      const groupDashboards = orderedDashboardsByGroup[group]
      if (groupDashboards.length <= 1) return
      if (isNotDefined(toIndex) || isNotDefined(movedDashboard)) return
      if (movedDashboard.editable !== true || movedDashboard.home === true) return
      if (getDashboardGroup(movedDashboard) !== group) return

      const newOrder = getNewOrder(
        groupDashboards.filter((dashboard) => dashboard.dashboardId !== movedDashboard.dashboardId),
        toIndex
      )
      if (newOrder === movedDashboard.order) return

      const updatedDashboard = { ...movedDashboard, order: newOrder }
      setPendingOrders((previous) => new Map(previous).set(movedDashboard.dashboardId, newOrder))
      cache.setItem(updatedDashboard)
      update(updatedDashboard)
        .catch(() => cache.setItem(movedDashboard))
        .finally(() => {
          setPendingOrders((previous) => {
            const next = new Map(previous)
            next.delete(movedDashboard.dashboardId)
            return next
          })
        })
    },
    [cache, dragToIndex, draggingDashboard, handleDragEnd, orderedDashboardsByGroup, update]
  )

  const handleDelete = useCallback(
    async (dashboard: DashboardDataWithEditable): Promise<void> => {
      const confirmed = await confirmDelete({
        title: t('Delete Dashboard'),
        message: t('"{{name}}" will be removed for everyone.', {
          name: dashboard.name?.trim() || t('This dashboard'),
        }),
        t,
      })
      if (!confirmed) return

      setDeletingDashboardId(dashboard.dashboardId)
      try {
        await remove({ dashboardId: dashboard.dashboardId })
      } catch {
        return
      } finally {
        setDeletingDashboardId(undefined)
      }
    },
    [remove, t]
  )

  const resetInteractionState = useCallback((): void => {
    setIsReordering(false)
    setDraggingDashboard(undefined)
    setDragToIndex(undefined)
    setDeletingDashboardId(undefined)
    setPendingOrders(new Map())
  }, [])

  return {
    deletingDashboardId,
    displayDashboardsByGroup,
    draggingDashboardId: draggingDashboard?.dashboardId,
    handleDelete,
    handleDragEnd,
    handleDragOver,
    handleDragStart,
    handleDrop,
    hasEditableDashboard: orderedDashboards.some((dashboard) => dashboard.editable === true && dashboard.home !== true),
    isRemoving,
    isReordering,
    orderedDashboards,
    resetInteractionState,
    startReordering,
    stopReordering,
  }
}
